import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, Paper, MenuItem, IconButton,
  CircularProgress, Switch, Chip, Collapse, Divider, Autocomplete, alpha, Fade, Grow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ScaleIcon from "@mui/icons-material/Scale";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InventoryIcon from "@mui/icons-material/Inventory";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useFormik } from "formik";
import { HexColorPicker } from "react-colorful";
import {
  useCreateProductMutation,
  useGetAllMainCategoryQuery,
  useGetModifierGroupQuery,
  useGetLastProductIdQuery,
  useGetProductBrandQuery,
  useGetProductTagQuery,
  useGetSubCategoryByArrayQuery,
  useGetUnitsQuery,
  useCreateUnitMutation,
  useGetTaxGroupsQuery,
  useCreateProductBrandMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  ICategory, ILastProductId, IModifierGroupPopUp,
  IProduct, IProductBrand, IProductTag,
} from "../../../Api/Interface/api.interface";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CategoryCascade from "../../../Components/CategoryCascade";
import { useNavigate } from "react-router";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const labelSx = {
  fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75,
  letterSpacing: "0.02em", textTransform: "uppercase" as const,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e2e8f0", transition: "all 0.2s ease" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 2 },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,131,143,0.08)" },
  },
};

// Same as inputSx, but hides the native number-input spinner arrows
const numInputSx = {
  ...inputSx,
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
  "& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
};

const Section = ({
  icon, title, badge, children, collapsible, defaultOpen = true, accentColor = "#00838f", subtitle,
}: {
  icon: React.ReactNode; title: string; badge?: string; children: React.ReactNode;
  collapsible?: boolean; defaultOpen?: boolean; accentColor?: string; subtitle?: string;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3, borderRadius: 3, border: "1px solid #e2e8f0",
        overflow: "hidden", transition: "all 0.3s ease",
        "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.04)} 0%, ${alpha(accentColor, 0.01)} 100%)`,
          borderBottom: open ? `1px solid ${alpha(accentColor, 0.08)}` : "none",
          px: 3, py: 2.5,
          cursor: collapsible ? "pointer" : "default",
          transition: "all 0.2s ease",
          "&:hover": collapsible ? { background: `linear-gradient(135deg, ${alpha(accentColor, 0.06)} 0%, ${alpha(accentColor, 0.02)} 100%)` } : {},
        }}
        onClick={() => collapsible && setOpen(v => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha(accentColor, 0.12)} 0%, ${alpha(accentColor, 0.06)} 100%)`,
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontSize={15} fontWeight={700} color="#0f172a" letterSpacing="-0.01em">{title}</Typography>
              {badge && (
                <Chip label={badge} size="small" sx={{
                  height: 20, fontSize: 9, fontWeight: 700,
                  background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                  color: "#dc2626", border: "none",
                }} />
              )}
            </Box>
            {subtitle && <Typography fontSize={11} color="#94a3b8" mt={0.25}>{subtitle}</Typography>}
          </Box>
          {collapsible && (
            <ExpandMoreIcon sx={{
              fontSize: 20, color: "#94a3b8",
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: open ? "rotate(180deg)" : "rotate(0)",
            }} />
          )}
        </Box>
      </Box>
      {collapsible ? (
        <Collapse in={open}>
          <Box sx={{ p: 3 }}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={{ p: 3 }}>{children}</Box>
      )}
    </Paper>
  );
};

const LARAVEL_API_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

interface ProductFormProps {
  mode?: "create" | "edit";
  initialData?: any;
  productId?: number | string;
}

const ProductForm: React.FC<ProductFormProps> = ({ mode = "create", initialData, productId }) => {
  const navigate = useNavigate();
  const [newProduct, { isLoading }] = useCreateProductMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const isEdit = mode === "edit";

  // Data queries
  const { data: categoryData } = useGetAllMainCategoryQuery({ departmentId: "", coursingId: "", roleId: null, taxeId: "", createdDateStart: null, createdDateEnd: null, searchText: "", numberOfLength: 0, isActive: true, categoryIsLooping: null, createdDate: null });
  const { data: productBrandData, refetch: refetchProductBrands } = useGetProductBrandQuery({ searchText: "", isActive: true });
  const { data: productTagData } = useGetProductTagQuery({ searchText: "", isActive: true });
  const { data: modifierGroupData } = useGetModifierGroupQuery({ productBrandId: "", productCategoryId: "", productTagIds: [], modifierViewOnline: "", createdDateStart: null, createdDateEnd: null, searchText: "", createdDate: "", id: null });
  const { data: newLastProductIdData } = useGetLastProductIdQuery();

  // Category state
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | null>(null);
  const [selectedSubToSubCategoryId, setSelectedSubToSubCategoryId] = useState<number | null>(null);
  const [selectedSubToSubToSubCategoryId, setSelectedSubToSubToSubCategoryId] = useState<number | null>(null);

  const { data: subCategoryData } = useGetSubCategoryByArrayQuery({ parent_ids: selectedCategoryId ? [Number(selectedCategoryId)] : [] });
  const { data: subCategory2Data } = useGetSubCategoryByArrayQuery({ parent_ids: selectedSubCategoryId ? [Number(selectedCategoryId), Number(selectedSubCategoryId)] : [] });
  const { data: subCategory3Data } = useGetSubCategoryByArrayQuery({ parent_ids: selectedSubCategoryId ? [Number(selectedCategoryId), Number(selectedSubCategoryId), Number(selectedSubToSubCategoryId)] : [] });

  const categoryList = useMemo(() => categoryData?.data as ICategory[] || [], [categoryData?.data]);
  const subCategoryList = useMemo(() => subCategoryData?.data as ICategory[] || [], [subCategoryData?.data]);
  const subToSubCategoryList = useMemo(() => subCategory2Data?.data as ICategory[] || [], [subCategory2Data?.data]);
  const subToSubToSubCategoryList = useMemo(() => subCategory3Data?.data as ICategory[] || [], [subCategory3Data?.data]);
  const productBrandList = useMemo(() => productBrandData?.data as IProductBrand[] || [], [productBrandData?.data]);
  const productTagList = useMemo(() => productTagData?.data as IProductTag[] || [], [productTagData?.data]);
  const modifierGroupList = useMemo(() => modifierGroupData?.data as IModifierGroupPopUp[] || [], [modifierGroupData?.data]);
  const lastProductId = useMemo(() => {
    return (newLastProductIdData?.data as ILastProductId)?.lastInsertedId ?? 0;
  }, [newLastProductIdData?.data]);

  // UI state
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [selectedColor, setSelectedColor] = useState("#FFFFFF");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const colorRef = useRef<HTMLDivElement>(null);

  // Units + Tax Groups
  const { data: unitsData, refetch: refetchUnits } = useGetUnitsQuery();
  const { data: taxGroupsData } = useGetTaxGroupsQuery();
  const taxGroupList = useMemo(() => (taxGroupsData?.data || []) as { id: number; name: string; rate: number }[], [taxGroupsData?.data]);
  const [createUnit] = useCreateUnitMutation();
  const unitList = useMemo(() => (unitsData?.data || []) as { id: number; name: string; abbreviation: string }[], [unitsData?.data]);
  const [quickAddUnitOpen, setQuickAddUnitOpen] = useState(false);
  const [newUnitName, setNewUnitName] = useState("");
  const [newUnitAbbr, setNewUnitAbbr] = useState("");
  const [quickAddUnitTarget, setQuickAddUnitTarget] = useState<"base" | "purchase">("base");
  const [unitError, setUnitError] = useState("");

  // Quick-add Brand state
  const [createProductBrand, { isLoading: isCreatingBrand }] = useCreateProductBrandMutation();
  const [quickAddBrandOpen, setQuickAddBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [brandError, setBrandError] = useState("");

  // Supplier state
  const [suppliers, setSuppliers] = useState<{ id: number; customerCompanyName: string }[]>([]);
  const [supplierMappings, setSupplierMappings] = useState<{ supplier_id: number; supplier_price: number | string; supplier_unit: string; is_default: boolean }[]>([]);

  // Quick-create supplier dialog
  const [supplierCreateOpen, setSupplierCreateOpen] = useState(false);
  const [supplierCreateRowIdx, setSupplierCreateRowIdx] = useState<number | null>(null);
  const [supplierCreateBusy, setSupplierCreateBusy] = useState(false);
  const [supplierCreateError, setSupplierCreateError] = useState("");
  const [newSupplierForm, setNewSupplierForm] = useState({ customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "" });

  const loadSuppliers = async (): Promise<{ id: number; customerCompanyName: string }[]> => {
    try {
      const t = localStorage.getItem("token") || "";
      const res = await fetch(`${API_URL}/vendor/getAll`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ searchText: "" }),
      });
      const json = await res.json();
      const list = (json?.data || [])
        .filter((s: any) => !s.isDelete)
        .map((s: any) => ({ id: s.id, customerCompanyName: s.customerCompanyName || `Supplier #${s.id}` }));
      setSuppliers(list);
      return list;
    } catch {
      return [];
    }
  };

  useEffect(() => { loadSuppliers(); }, []);

  const defaultValues: IProduct = {
    productName: "", modelNo: "", productCategoryId: "", mainCatId: "",
    productDetailsIsLooping: false, productsDetailsLoopingConstant: "",
    productShortDescription: "", productLongDescription: "",
    productConversionUnit: [], saleTypeId: [], stockManagementType: String(2),
    productBrandId: "", productTagIds: [], productViewOnline: true,
    productViewPos: false, isActive: true, isSerialize: false, isModifier: false,
    productPrinterIds: [], productIcon: "", productButtonColor: "",
    productBarcode: "", productManualBarcode: "", productCanSell: true,
    productImg: "", productPrice: "", hasVariants: false, variantIds: "",
    salePrice: "", offerPrice: "", wholesaleprice: "", qty: 0, orderNumber: "",
    modifierGroupId: [],
    base_unit_id: "", purchase_unit_id: "", conversion_rate: "",
    is_weight_based: false, tax_group_id: "", pos_requires_quantity: false,
  };

  // Prefill from initialData for edit mode
  const [existingImage, setExistingImage] = useState<string>("");
  const [dataLoaded, setDataLoaded] = useState(!isEdit);

  useEffect(() => {
    if (isEdit && initialData) {
      formik.setValues({
        ...defaultValues,
        productName: initialData.productName || "",
        modelNo: initialData.modelNo || "",
        productCategoryId: initialData.productCategoryId || "",
        mainCatId: initialData.mainCatId || "",
        productDetailsIsLooping: initialData.productDetailsIsLooping || false,
        productsDetailsLoopingConstant: initialData.productsDetailsLoopingConstant || "",
        productShortDescription: initialData.productShortDescription || "",
        productLongDescription: initialData.productLongDescription || "",
        productConversionUnit: initialData.productConversionUnit || [],
        saleTypeId: initialData.saleTypeId || [],
        stockManagementType: String(initialData.stockManagementType ?? 2),
        productBrandId: initialData.productBrandId || "",
        productTagIds: initialData.productTagIds || [],
        productViewOnline: initialData.productViewOnline ?? true,
        productViewPos: initialData.productViewPos ?? false,
        isActive: initialData.isActive ?? true,
        isSerialize: initialData.isSerialize ?? false,
        isModifier: Boolean(initialData.isModifier),
        productPrinterIds: initialData.productPrinterIds || [],
        productIcon: initialData.productIcon || "",
        productButtonColor: initialData.productButtonColor || "",
        productBarcode: initialData.productBarcode || "",
        productManualBarcode: initialData.productManualBarcode || "",
        productCanSell: initialData.productCanSell ?? true,
        productImg: initialData.productImg || "",
        productPrice: initialData.productPrice || "",
        hasVariants: initialData.hasVariants ?? false,
        variantIds: initialData.variantIds || "",
        salePrice: initialData.salePrice || "",
        offerPrice: initialData.offerPrice || "",
        wholesaleprice: initialData.wholesaleprice || "",
        qty: initialData.qty || 0,
        orderNumber: initialData.orderNumber || "",
        modifierGroupId: initialData.modifierGroupId || [],
        base_unit_id: initialData.base_unit_id || "",
        purchase_unit_id: initialData.purchase_unit_id || "",
        conversion_rate: initialData.conversion_rate ?? "",
        is_weight_based: initialData.is_weight_based ?? false,
        tax_group_id: initialData.tax_group_id || "",
        pos_requires_quantity: initialData.pos_requires_quantity ?? false,
      });
      // Set UI state from initial data
      if (initialData.productButtonColor) setSelectedColor(initialData.productButtonColor);
      if (initialData.productImg) setExistingImage(initialData.productImg);
      // Inventory
      if (initialData.track_inventory !== undefined) {
        const num = (v: any) => (v === null || v === undefined || v === "" ? "" : String(v));
        setInventoryForm({
          track_inventory: initialData.track_inventory,
          current_stock: num(initialData.current_stock),
          minimum_stock: num(initialData.minimum_stock),
          reorder_point: num(initialData.reorder_point),
          reorder_qty: num(initialData.reorder_qty),
        });
      }
      // Suppliers
      if (initialData.supplier_mappings?.length > 0) {
        setSupplierMappings(initialData.supplier_mappings.map((m: any) => ({
          supplier_id: m.supplier_id, supplier_price: m.supplier_price, supplier_unit: m.supplier_unit || "", is_default: m.is_default,
        })));
      }
      // Category
      if (initialData.productCategoryId) {
        setSelectedCategoryId(Number(initialData.productCategoryId));
      }
      setDataLoaded(true);
    }
  }, [isEdit, initialData]);

  const formik = useFormik<IProduct>({
    initialValues: defaultValues,
    enableReinitialize: false,
    onSubmit: async (values, { resetForm }) => {
      try {
        const productCategoryId = selectedSubToSubToSubCategoryId || selectedSubToSubCategoryId || selectedSubCategoryId || selectedCategoryId || values.mainCatId;

        let productImg = isEdit ? (existingImage || "") : "";
        if (selectedImage) productImg = (await uploadImage(selectedImage)) || "";

        const temData: any = {
          productName: values.productName, productCategoryId,
          productDetailsIsLooping: values.productDetailsIsLooping,
          productsDetailsLoopingConstant: values.productsDetailsLoopingConstant ? Number(values.productsDetailsLoopingConstant) : null,
          productShortDescription: values.productShortDescription,
          productLongDescription: values.productLongDescription,
          productConversionUnit: Array.isArray(values.productConversionUnit) ? values.productConversionUnit : (typeof values.productConversionUnit === 'string' && values.productConversionUnit ? JSON.parse(values.productConversionUnit) : []),
          productBrandId: values.productBrandId,
          productTagIds: Array.isArray(values.productTagIds) ? values.productTagIds : (typeof values.productTagIds === 'string' && values.productTagIds ? JSON.parse(values.productTagIds) : []),
          productViewOnline: values.productViewOnline, productViewPos: values.productViewPos,
          isActive: values.isActive,
          productPrinterIds: Array.isArray(values.productPrinterIds) ? values.productPrinterIds : (typeof values.productPrinterIds === 'string' && values.productPrinterIds ? JSON.parse(values.productPrinterIds) : []),
          productIcon: values.productIcon, productButtonColor: selectedColor,
          productBarcode: values.productBarcode, productManualBarcode: values.productManualBarcode,
          productCanSell: values.productCanSell, productPrice: values.productPrice,
          saleTypeId: values.saleTypeId, stockManagementType: values.stockManagementType,
          modelNo: values.modelNo, isSerialize: values.isSerialize,
          isModifier: values.isModifier, productImg,
          hasVariants: values.hasVariants, variantIds: values.variantIds || "",
          salePrice: values.salePrice, offerPrice: values.offerPrice,
          wholesaleprice: values.wholesaleprice, qty: values.qty,
          orderNumber: values.orderNumber, modifierGroupId: values.modifierGroupId,
          base_unit_id: values.base_unit_id || null,
          purchase_unit_id: values.purchase_unit_id || null,
          conversion_rate: (
            values.base_unit_id &&
            values.purchase_unit_id &&
            String(values.base_unit_id) === String(values.purchase_unit_id)
          )
            ? 1
            : (values.conversion_rate !== "" ? Number(values.conversion_rate) : null),
          is_weight_based: values.is_weight_based || false,
          tax_group_id: values.tax_group_id || null,
          pos_requires_quantity: values.pos_requires_quantity || false,
        };

        // Edit mode: include id
        if (isEdit && productId) temData.id = productId;

        const res = await newProduct(temData).unwrap();
        if (!res.status) {
          // Barcode conflict from a race — route into inline state, do not toast
          const c = (res as any).conflict as { barcode: string; productId: number; productName: string } | undefined;
          if (c?.barcode) {
            if (c.barcode === values.productBarcode) {
              setBarcodeConflicts((s) => ({ ...s, system: c }));
            } else if (c.barcode === values.productManualBarcode) {
              setBarcodeConflicts((s) => ({ ...s, manual: c }));
            } else {
              setBarcodeConflicts((s) => ({ ...s, system: c }));
            }
            return;
          }
          showErrorMessage(res.message);
          return;
        }
        showMessage(isEdit ? "Product updated successfully" : res.message);

        const savedId = res.data?.id || productId;

        // Save supplier mappings
        if (supplierMappings.length > 0 && savedId) {
          const t = localStorage.getItem("token") || "";
          const headers = { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
          for (const m of supplierMappings) {
            try {
              await fetch(`${API_URL}/vendor/${m.supplier_id}/products`, {
                method: "POST", headers,
                body: JSON.stringify({
                  product_id: savedId,
                  supplier_price: m.supplier_price === "" || m.supplier_price === null ? 0 : Number(m.supplier_price),
                  supplier_unit: m.supplier_unit,
                  is_default: m.is_default,
                }),
              });
            } catch {}
          }
        }

        // Save inventory settings — always when editing or when any value is set
        if (savedId) {
          const t = localStorage.getItem("token") || "";
          const toNum = (v: string) => (v === "" ? 0 : Number(v));
          try {
            await fetch(`${API_URL}/warehouse/product/${savedId}/settings`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
              body: JSON.stringify({
                track_inventory: inventoryForm.track_inventory,
                current_stock: toNum(inventoryForm.current_stock),
                minimum_stock: toNum(inventoryForm.minimum_stock),
                reorder_point: toNum(inventoryForm.reorder_point),
                reorder_qty: toNum(inventoryForm.reorder_qty),
              }),
            });
          } catch {}
        }

        if (!isEdit) {
          resetForm(); setSelectedImage(undefined); setSelectedCategoryId(null);
          setSelectedSubCategoryId(null); setSelectedSubToSubCategoryId(null);
          setSelectedSubToSubToSubCategoryId(null); setSupplierMappings([]);
        }
        navigate("/product/list");
      } catch (err: any) {
        const msg = err?.data?.message || err?.message || "Something went wrong";
        showErrorMessage(msg);
        console.error("Product save error:", err);
      }
    },
  });

  // Inventory form — numeric fields kept as strings so empty input is allowed
  const [inventoryForm, setInventoryForm] = useState<{
    track_inventory: boolean;
    current_stock: string;
    minimum_stock: string;
    reorder_point: string;
    reorder_qty: string;
  }>({ track_inventory: false, current_stock: "", minimum_stock: "", reorder_point: "", reorder_qty: "" });

  // Auto-sync conversion_rate to 1 when base unit === purchase unit
  useEffect(() => {
    const baseId = formik.values.base_unit_id;
    const purchaseId = formik.values.purchase_unit_id;
    if (baseId && purchaseId && String(baseId) === String(purchaseId)) {
      if (Number(formik.values.conversion_rate) !== 1) {
        formik.setFieldValue("conversion_rate", 1);
      }
    }
  }, [formik.values.base_unit_id, formik.values.purchase_unit_id]);

  // ── Live barcode uniqueness check ──
  type BarcodeConflict = { barcode: string; productId: number; productName: string } | null;
  const [barcodeConflicts, setBarcodeConflicts] = useState<{ system: BarcodeConflict; manual: BarcodeConflict }>({ system: null, manual: null });
  const [barcodeChecking, setBarcodeChecking] = useState<{ system: boolean; manual: boolean }>({ system: false, manual: false });

  const checkBarcodeRemote = async (value: string, signal: AbortSignal): Promise<BarcodeConflict> => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const res = await fetch(`${API_URL}/products/check-barcode`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({
        barcode: trimmed,
        excludeProductId: isEdit && productId ? Number(productId) : undefined,
      }),
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.conflict ?? null;
  };

  // Debounced check for SYSTEM barcode field
  useEffect(() => {
    const value = formik.values.productBarcode;
    if (!value || !value.trim()) {
      setBarcodeConflicts((c) => ({ ...c, system: null }));
      setBarcodeChecking((c) => ({ ...c, system: false }));
      return;
    }
    const ctrl = new AbortController();
    setBarcodeChecking((c) => ({ ...c, system: true }));
    const t = setTimeout(async () => {
      try {
        const conflict = await checkBarcodeRemote(value, ctrl.signal);
        setBarcodeConflicts((c) => ({ ...c, system: conflict }));
      } catch { /* aborted or network error — leave previous state */ }
      finally { setBarcodeChecking((c) => ({ ...c, system: false })); }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [formik.values.productBarcode, isEdit, productId]);

  // Debounced check for MANUAL barcode field
  useEffect(() => {
    const value = formik.values.productManualBarcode;
    if (!value || !value.trim()) {
      setBarcodeConflicts((c) => ({ ...c, manual: null }));
      setBarcodeChecking((c) => ({ ...c, manual: false }));
      return;
    }
    const ctrl = new AbortController();
    setBarcodeChecking((c) => ({ ...c, manual: true }));
    const t = setTimeout(async () => {
      try {
        const conflict = await checkBarcodeRemote(value, ctrl.signal);
        setBarcodeConflicts((c) => ({ ...c, manual: conflict }));
      } catch { /* aborted or network error */ }
      finally { setBarcodeChecking((c) => ({ ...c, manual: false })); }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [formik.values.productManualBarcode, isEdit, productId]);

  const hasBarcodeError = !!(barcodeConflicts.system || barcodeConflicts.manual);

  // Price validation
  const priceError = useMemo(() => {
    const cost = Number(formik.values.productPrice) || 0;
    const instore = Number(formik.values.salePrice) || 0;
    const wholesale = Number(formik.values.wholesaleprice) || 0;
    if (cost > 0 && instore > 0 && cost > instore) return "Cost price cannot exceed instore price";
    if (cost > 0 && wholesale > 0 && cost > wholesale) return "Cost price cannot exceed wholesale price";
    return "";
  }, [formik.values.productPrice, formik.values.salePrice, formik.values.wholesaleprice]);

  const formValid = useMemo(() => {
    if (!formik.values.productName.trim()) return false;
    const sameUnit =
      !!formik.values.base_unit_id &&
      !!formik.values.purchase_unit_id &&
      String(formik.values.base_unit_id) === String(formik.values.purchase_unit_id);
    if (
      formik.values.purchase_unit_id &&
      !sameUnit &&
      !formik.values.conversion_rate &&
      formik.values.conversion_rate !== 0
    ) return false;
    if (priceError) return false;
    if (hasBarcodeError) return false;
    return true;
  }, [formik.values.productName, formik.values.purchase_unit_id, formik.values.base_unit_id, formik.values.conversion_rate, priceError, hasBarcodeError]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setSelectedImage(file);
  };

  const addSupplierMapping = () => {
    setSupplierMappings(prev => [...prev, { supplier_id: 0, supplier_price: "", supplier_unit: "", is_default: prev.length === 0 }]);
  };

  // Category breadcrumb for visual feedback
  const categoryBreadcrumb = useMemo(() => {
    const parts: string[] = [];
    if (selectedCategoryId) {
      const cat = categoryList.find(c => c.id === selectedCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    if (selectedSubCategoryId) {
      const cat = subCategoryList.find(c => c.id === selectedSubCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    if (selectedSubToSubCategoryId) {
      const cat = subToSubCategoryList.find(c => c.id === selectedSubToSubCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    if (selectedSubToSubToSubCategoryId) {
      const cat = subToSubToSubCategoryList.find(c => c.id === selectedSubToSubToSubCategoryId);
      if (cat) parts.push(cat.categoryName);
    }
    return parts;
  }, [selectedCategoryId, selectedSubCategoryId, selectedSubToSubCategoryId, selectedSubToSubToSubCategoryId, categoryList, subCategoryList, subToSubCategoryList, subToSubToSubCategoryList]);

  useEffect(() => {
    if (isEdit) return;
    const nextId = (lastProductId || 0) + 1;
    const newProductBarcode = "BR" + String(nextId).padStart(9, "0");
    formik.setFieldValue("productBarcode", newProductBarcode);
  }, [isEdit, lastProductId]);

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      {/* ── Top Bar ── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 0, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mx: "auto", py: 2 }}>
          <IconButton onClick={() => navigate("/product/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.3)" },
              transition: "all 0.2s ease",
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                boxShadow: "0 2px 8px rgba(0,131,143,0.4)",
              }}>
                <ShoppingCartIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                {isEdit ? "Edit Product" : "New Product"}
              </Typography>
            </Box>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" mt={0.25} ml={5.5}>
              Fill in the details to create a new product
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => navigate("/product/list")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8", px: 2.5,
              "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
              transition: "all 0.2s ease",
            }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation size="small" disabled={!formValid || isLoading}
            onClick={() => formik.handleSubmit()}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)", boxShadow: "0 4px 20px rgba(0,131,143,0.5)" },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
              transition: "all 0.2s ease",
            }}>
            {isLoading ? "Saving..." : isEdit ? "Update Product" : "Save Product"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ mx: "auto", p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* LEFT COLUMN */}
            <Grid item xs={12} lg={8}>

              {/* A. Basic Information */}
              <Section
                icon={<CategoryIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
                title="Basic Information"
                badge="Required"
                accentColor="#3b82f6"
                subtitle="Product name, category, and description"
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Typography sx={labelSx}>Product Name *</Typography>
                    <TextField
                      fullWidth placeholder="Enter product name"
                      {...formik.getFieldProps("productName")}
                      sx={{
                        ...inputSx,
                        "& .MuiOutlinedInput-root": {
                          ...inputSx["& .MuiOutlinedInput-root"],
                          fontSize: 15, fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>

                  {/* Category — Cascading Selector */}
                  <Grid item xs={12}>
                    <CategoryCascade
                      initialCategoryId={isEdit && initialData?.productCategoryId ? Number(initialData.productCategoryId) : null}
                      onCategoryChange={(catId) => {
                        setSelectedCategoryId(catId);
                        setSelectedSubCategoryId(null);
                        setSelectedSubToSubCategoryId(null);
                        setSelectedSubToSubToSubCategoryId(null);
                      }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ ...labelSx, mb: 0 }}>Brand</Typography>
                      <IconButton
                        size="small"
                        onClick={() => { setQuickAddBrandOpen(true); setBrandError(""); setNewBrandName(""); }}
                        sx={{ width: 22, height: 22, "&:hover": { bgcolor: alpha("#00838f", 0.08) } }}
                        title="Create new brand"
                      >
                        <AddIcon sx={{ fontSize: 14, color: "#00838f" }} />
                      </IconButton>
                    </Box>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      {...formik.getFieldProps("productBrandId")}
                      sx={inputSx}
                    >
                      <MenuItem value="">No Brand</MenuItem>
                      {productBrandList?.map(b => (
                        <MenuItem key={b.id} value={b.id}>{b.productBrandName}</MenuItem>
                      ))}
                      <MenuItem
                        value="__create_new_brand__"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQuickAddBrandOpen(true);
                          setBrandError("");
                          setNewBrandName("");
                        }}
                        sx={{
                          color: "#00838f",
                          fontWeight: 600,
                          borderTop: "1px solid #e2e8f0",
                          mt: 0.5,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16, mr: 1 }} />
                        Create new brand
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Model / SKU</Typography>
                    <TextField fullWidth size="small" placeholder="Enter model number" {...formik.getFieldProps("modelNo")} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={labelSx}>Short Description</Typography>
                    <TextField
                      fullWidth size="small" multiline rows={3}
                      placeholder="Brief product description..."
                      {...formik.getFieldProps("productShortDescription")} sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Section>

              {/* A2. Barcode */}
              <Section
                icon={<QrCodeIcon sx={{ fontSize: 18, color: "#00838f" }} />}
                title="Barcode"
                accentColor="#00838f"
                subtitle="System and manual barcodes"
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ ...labelSx, fontSize: 11 }}>System Barcode</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Auto-generated"
                      {...formik.getFieldProps("productBarcode")}
                      error={!!barcodeConflicts.system}
                      helperText={
                        barcodeConflicts.system
                          ? `Barcode already exists for product: ${barcodeConflicts.system.productName}`
                          : (barcodeChecking.system ? "Checking…" : " ")
                      }
                      InputProps={{
                        startAdornment: <QrCodeIcon sx={{ fontSize: 16, color: barcodeConflicts.system ? "#dc2626" : "#94a3b8", mr: 1 }} />,
                      }}
                      sx={{
                        ...inputSx,
                        ...(barcodeConflicts.system && {
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#fef2f2",
                            "& fieldset": { borderColor: "#dc2626", borderWidth: 1.5 },
                            "&:hover fieldset": { borderColor: "#dc2626" },
                            "&.Mui-focused fieldset": { borderColor: "#dc2626", borderWidth: 1.5 },
                          },
                        }),
                        "& .MuiFormHelperText-root": {
                          fontSize: 11,
                          fontWeight: barcodeConflicts.system ? 600 : 400,
                          color: barcodeConflicts.system ? "#dc2626" : "#94a3b8",
                          ml: 0,
                          mt: 0.5,
                          minHeight: 16,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ ...labelSx, fontSize: 11 }}>Manual Barcode</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Scan or enter"
                      {...formik.getFieldProps("productManualBarcode")}
                      error={!!barcodeConflicts.manual}
                      helperText={
                        barcodeConflicts.manual
                          ? `Barcode already exists for product: ${barcodeConflicts.manual.productName}`
                          : (barcodeChecking.manual ? "Checking…" : " ")
                      }
                      InputProps={{
                        startAdornment: <QrCodeIcon sx={{ fontSize: 16, color: barcodeConflicts.manual ? "#dc2626" : "#94a3b8", mr: 1 }} />,
                      }}
                      sx={{
                        ...inputSx,
                        ...(barcodeConflicts.manual && {
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#fef2f2",
                            "& fieldset": { borderColor: "#dc2626", borderWidth: 1.5 },
                            "&:hover fieldset": { borderColor: "#dc2626" },
                            "&.Mui-focused fieldset": { borderColor: "#dc2626", borderWidth: 1.5 },
                          },
                        }),
                        "& .MuiFormHelperText-root": {
                          fontSize: 11,
                          fontWeight: barcodeConflicts.manual ? 600 : 400,
                          color: barcodeConflicts.manual ? "#dc2626" : "#94a3b8",
                          ml: 0,
                          mt: 0.5,
                          minHeight: 16,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Section>

              {/* B. Pricing */}
              <Section
                icon={<AttachMoneyIcon sx={{ fontSize: 18, color: "#f59e0b" }} />}
                title="Pricing"
                accentColor="#f59e0b"
                subtitle="Set cost, wholesale, and retail prices"
              >
                <Grid container spacing={2.5}>
                  {[
                    { label: "Cost Price", field: "productPrice", icon: "$", color: "#ef4444", hint: "Your purchase cost" },
                    { label: "Wholesale Price", field: "wholesaleprice", icon: "$", color: "#8b5cf6", hint: "Bulk buyer price" },
                    { label: "Instore (POS) Price", field: "salePrice", icon: "$", color: "#16a34a", hint: "Retail selling price" },
                  ].map((p) => (
                    <Grid item xs={12} sm={4} key={p.field}>
                      <Box sx={{
                        p: 2, borderRadius: 2.5, border: "1px solid #e2e8f0",
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: alpha(p.color, 0.3), boxShadow: `0 2px 12px ${alpha(p.color, 0.08)}` },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: alpha(p.color, 0.08),
                          }}>
                            <Typography fontSize={13} fontWeight={800} color={p.color}>{p.icon}</Typography>
                          </Box>
                          <Box>
                            <Typography fontSize={12} fontWeight={700} color="#334155">{p.label}</Typography>
                            <Typography fontSize={10} color="#94a3b8">{p.hint}</Typography>
                          </Box>
                        </Box>
                        <TextField
                          fullWidth size="small" type="number" placeholder="0.00"
                          inputProps={{ step: "0.01", min: "0" }}
                          value={(formik.values as any)[p.field]}
                          onChange={(e) => formik.setFieldValue(p.field, e.target.value)}
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val)) formik.setFieldValue(p.field, val.toFixed(2));
                          }}
                          name={p.field}
                          sx={{
                            ...inputSx,
                            "& .MuiOutlinedInput-root": {
                              ...inputSx["& .MuiOutlinedInput-root"],
                              fontSize: 18, fontWeight: 700,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {priceError && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca", display: "flex", alignItems: "center", gap: 1 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 16, color: "#dc2626" }} />
                    <Typography fontSize={12} fontWeight={600} color="#dc2626">{priceError}</Typography>
                  </Box>
                )}
              </Section>

              {/* C. Units & Conversion */}
              <Section
                icon={<ScaleIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />}
                title="Units & Conversion"
                accentColor="#8b5cf6"
                subtitle="Define measurement units and conversion rates"
                collapsible
                defaultOpen={false}
              >
                <Grid container spacing={2.5}>
                  {/* Base Unit */}
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ ...labelSx, mb: 0 }}>Base Unit *</Typography>
                      <IconButton size="small" onClick={() => { setQuickAddUnitTarget("base"); setQuickAddUnitOpen(true); }}
                        sx={{ width: 22, height: 22, "&:hover": { bgcolor: alpha("#8b5cf6", 0.08) } }}>
                        <AddIcon sx={{ fontSize: 14, color: "#8b5cf6" }} />
                      </IconButton>
                    </Box>
                    <TextField select fullWidth size="small"
                      value={formik.values.base_unit_id || ""}
                      onChange={e => {
                        const val = e.target.value;
                        formik.setFieldValue("base_unit_id", val);
                        // Smart: if same unit selected, auto-set conversion = 1
                        if (val && val === String(formik.values.purchase_unit_id)) {
                          formik.setFieldValue("conversion_rate", 1);
                        }
                      }}
                      sx={inputSx}
                    >
                      <MenuItem value="">Select base unit</MenuItem>
                      {unitList.map(u => <MenuItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</MenuItem>)}
                    </TextField>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                      The smallest unit of measure for this product
                    </Typography>
                  </Grid>

                  {/* Purchase Unit */}
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ ...labelSx, mb: 0 }}>Purchase Unit</Typography>
                      <IconButton size="small" onClick={() => { setQuickAddUnitTarget("purchase"); setQuickAddUnitOpen(true); }}
                        sx={{ width: 22, height: 22, "&:hover": { bgcolor: alpha("#8b5cf6", 0.08) } }}>
                        <AddIcon sx={{ fontSize: 14, color: "#8b5cf6" }} />
                      </IconButton>
                    </Box>
                    <TextField select fullWidth size="small"
                      value={formik.values.purchase_unit_id || ""}
                      onChange={e => {
                        const val = e.target.value;
                        formik.setFieldValue("purchase_unit_id", val);
                        if (!val) {
                          formik.setFieldValue("conversion_rate", "");
                        } else if (val === String(formik.values.base_unit_id)) {
                          formik.setFieldValue("conversion_rate", 1);
                        }
                      }}
                      sx={inputSx}
                    >
                      <MenuItem value="">Select purchase unit</MenuItem>
                      {unitList.map(u => <MenuItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</MenuItem>)}
                    </TextField>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                      The unit you buy in (e.g. box, bag, crate)
                    </Typography>
                  </Grid>

                  {/* Conversion Rate */}
                  <Grid item xs={12} sm={4}>
                    <Typography sx={labelSx}>Conversion Rate</Typography>
                    <TextField fullWidth size="small" type="number"
                      placeholder="e.g. 10"
                      inputProps={{ step: "0.01", min: "0" }}
                      value={formik.values.conversion_rate}
                      onChange={e => formik.setFieldValue("conversion_rate", e.target.value)}
                      disabled={
                        !!formik.values.base_unit_id &&
                        !!formik.values.purchase_unit_id &&
                        String(formik.values.base_unit_id) === String(formik.values.purchase_unit_id)
                      }
                      error={
                        !!formik.values.purchase_unit_id &&
                        String(formik.values.base_unit_id) !== String(formik.values.purchase_unit_id) &&
                        !formik.values.conversion_rate &&
                        formik.values.conversion_rate !== 0
                      }
                      sx={inputSx}
                    />
                    {/* Inline validation error */}
                    {formik.values.purchase_unit_id &&
                      String(formik.values.base_unit_id) !== String(formik.values.purchase_unit_id) &&
                      !formik.values.conversion_rate &&
                      formik.values.conversion_rate !== 0 && (
                      <Typography sx={{ fontSize: 10, color: "#ef4444", mt: 0.5, fontWeight: 600 }}>
                        Conversion required when purchase unit is selected
                      </Typography>
                    )}
                    {/* Smart helper text */}
                    {!formik.values.purchase_unit_id && (
                      <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                        How many base units in 1 purchase unit
                      </Typography>
                    )}
                  </Grid>

                  {/* Conversion helper display */}
                  {formik.values.base_unit_id && formik.values.purchase_unit_id && formik.values.conversion_rate && (
                    <Grid item xs={12}>
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        p: 2, borderRadius: 2.5,
                        background: `linear-gradient(135deg, ${alpha("#8b5cf6", 0.04)} 0%, ${alpha("#8b5cf6", 0.01)} 100%)`,
                        border: `1px solid ${alpha("#8b5cf6", 0.12)}`,
                      }}>
                        <SyncAltIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                        <Typography fontSize={13} fontWeight={600} color="#334155">
                          1{" "}
                          {unitList.find(u => u.id === Number(formik.values.purchase_unit_id))?.abbreviation || "purchase unit"}
                          {" "}={" "}
                          {formik.values.conversion_rate}{" "}
                          {unitList.find(u => u.id === Number(formik.values.base_unit_id))?.abbreviation || "base unit"}
                        </Typography>
                        {String(formik.values.base_unit_id) === String(formik.values.purchase_unit_id) && (
                          <Chip label="Same unit" size="small" sx={{
                            height: 20, fontSize: 9, fontWeight: 700,
                            bgcolor: alpha("#8b5cf6", 0.08), color: "#8b5cf6",
                          }} />
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Quick Add Unit Modal */}
                <Dialog open={quickAddUnitOpen} onClose={() => { setQuickAddUnitOpen(false); setNewUnitName(""); setNewUnitAbbr(""); setUnitError(""); }}
                  PaperProps={{ sx: { borderRadius: 3, minWidth: 380 } }}>
                  <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                        background: `linear-gradient(135deg, ${alpha("#8b5cf6", 0.12)} 0%, ${alpha("#8b5cf6", 0.06)} 100%)`,
                      }}>
                        <ScaleIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                      </Box>
                      Quick Add Unit
                    </Box>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 2 }}>
                    <Typography sx={{ ...labelSx, mt: 1 }}>Unit Name *</Typography>
                    <TextField fullWidth size="small" placeholder="e.g. Carton" value={newUnitName}
                      onChange={e => { setNewUnitName(e.target.value); setUnitError(""); }} sx={inputSx} autoFocus />
                    <Typography sx={{ ...labelSx, mt: 2 }}>Abbreviation</Typography>
                    <TextField fullWidth size="small" placeholder="e.g. ctn" value={newUnitAbbr}
                      onChange={e => setNewUnitAbbr(e.target.value)} sx={inputSx} />
                    {unitError && (
                      <Typography sx={{ fontSize: 11, color: "#ef4444", mt: 1, fontWeight: 600 }}>{unitError}</Typography>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button size="small" onClick={() => { setQuickAddUnitOpen(false); setNewUnitName(""); setNewUnitAbbr(""); setUnitError(""); }}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" disableElevation
                      disabled={!newUnitName.trim()}
                      onClick={async () => {
                        try {
                          const res = await createUnit({ name: newUnitName.trim(), abbreviation: newUnitAbbr.trim() || newUnitName.trim().toLowerCase() }).unwrap();
                          if (res.status && res.data) {
                            await refetchUnits();
                            formik.setFieldValue(quickAddUnitTarget === "base" ? "base_unit_id" : "purchase_unit_id", (res.data as any).id);
                            setQuickAddUnitOpen(false); setNewUnitName(""); setNewUnitAbbr(""); setUnitError("");
                            showMessage("Unit created successfully");
                          } else {
                            setUnitError(res.message || "Failed to create unit");
                          }
                        } catch { setUnitError("Failed to create unit"); }
                      }}
                      sx={{
                        borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                        background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                        "&:hover": { background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)" },
                      }}>
                      Create & Select
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Quick Add Brand Modal */}
                <Dialog
                  open={quickAddBrandOpen}
                  onClose={() => { setQuickAddBrandOpen(false); setNewBrandName(""); setBrandError(""); }}
                  PaperProps={{ sx: { borderRadius: 3, minWidth: 380 } }}
                >
                  <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                        background: `linear-gradient(135deg, ${alpha("#00838f", 0.12)} 0%, ${alpha("#00838f", 0.06)} 100%)`,
                      }}>
                        <StorefrontIcon sx={{ fontSize: 16, color: "#00838f" }} />
                      </Box>
                      Quick Add Brand
                    </Box>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 2 }}>
                    <Typography sx={{ ...labelSx, mt: 1 }}>Brand Name *</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. Acme"
                      value={newBrandName}
                      onChange={e => { setNewBrandName(e.target.value); setBrandError(""); }}
                      sx={inputSx}
                      autoFocus
                      onKeyDown={async (e) => {
                        if (e.key === "Enter" && newBrandName.trim() && !isCreatingBrand) {
                          e.preventDefault();
                          (document.getElementById("quick-add-brand-save") as HTMLButtonElement | null)?.click();
                        }
                      }}
                    />
                    {brandError && (
                      <Typography sx={{ fontSize: 11, color: "#ef4444", mt: 1, fontWeight: 600 }}>{brandError}</Typography>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => { setQuickAddBrandOpen(false); setNewBrandName(""); setBrandError(""); }}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      id="quick-add-brand-save"
                      size="small"
                      variant="contained"
                      disableElevation
                      disabled={!newBrandName.trim() || isCreatingBrand}
                      onClick={async () => {
                        try {
                          const res = await createProductBrand({
                            productBrandName: newBrandName.trim(),
                            isActive: true,
                          }).unwrap();
                          if (res.status && res.data) {
                            const refetched = await refetchProductBrands();
                            const list = (refetched.data?.data || []) as IProductBrand[];
                            const created = list.find(
                              (b: any) => b.productBrandName?.toLowerCase() === newBrandName.trim().toLowerCase()
                            ) || (res.data as any);
                            if (created?.id) {
                              formik.setFieldValue("productBrandId", created.id);
                            }
                            setQuickAddBrandOpen(false);
                            setNewBrandName("");
                            setBrandError("");
                            showMessage("Brand created successfully");
                          } else {
                            setBrandError(res.message || "Failed to create brand");
                          }
                        } catch {
                          setBrandError("Failed to create brand");
                        }
                      }}
                      sx={{
                        borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                        background: "linear-gradient(135deg, #00838f 0%, #006064 100%)",
                        "&:hover": { background: "linear-gradient(135deg, #006064 0%, #004d54 100%)" },
                      }}
                    >
                      {isCreatingBrand ? "Creating..." : "Create & Select"}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Section>

              {/* D. POS Settings */}
              <Section
                icon={<SettingsIcon sx={{ fontSize: 18, color: "#0891b2" }} />}
                title="POS Settings"
                accentColor="#0891b2"
                subtitle="Weight-based selling, quantity prompts, and tax configuration"
                collapsible
                defaultOpen={false}
              >
                <Grid container spacing={2.5}>
                  {/* Tax Group */}
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Tax Group</Typography>
                    <TextField select fullWidth size="small"
                      value={formik.values.tax_group_id || ""}
                      onChange={e => formik.setFieldValue("tax_group_id", e.target.value || "")}
                      sx={inputSx}
                    >
                      <MenuItem value="">No tax group</MenuItem>
                      {taxGroupList.map(tg => (
                        <MenuItem key={tg.id} value={tg.id}>{tg.name} ({tg.rate}%)</MenuItem>
                      ))}
                    </TextField>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                      Tax applied at POS checkout
                    </Typography>
                  </Grid>

                  {/* Spacer */}
                  <Grid item xs={12} sm={6} />

                  {/* Weight-based toggle */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: 2, borderRadius: 2.5,
                      bgcolor: formik.values.is_weight_based ? alpha("#0891b2", 0.04) : "#f8fafc",
                      border: `1px solid ${formik.values.is_weight_based ? alpha("#0891b2", 0.15) : "#e2e8f0"}`,
                      transition: "all 0.3s ease",
                    }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600} color={formik.values.is_weight_based ? "#0891b2" : "#94a3b8"}>
                          Weight-Based Product
                        </Typography>
                        <Typography fontSize={10} color="#94a3b8">
                          Requires weighing at POS (opens scale modal)
                        </Typography>
                      </Box>
                      <Switch size="small"
                        checked={Boolean(formik.values.is_weight_based)}
                        onChange={(_, v) => {
                          formik.setFieldValue("is_weight_based", v);
                          // is_weight_based takes priority over pos_requires_quantity
                          if (v) formik.setFieldValue("pos_requires_quantity", false);
                        }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#0891b2" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#0891b2" },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Quantity prompt toggle */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: 2, borderRadius: 2.5,
                      bgcolor: formik.values.pos_requires_quantity ? alpha("#8b5cf6", 0.04) : "#f8fafc",
                      border: `1px solid ${formik.values.pos_requires_quantity ? alpha("#8b5cf6", 0.15) : "#e2e8f0"}`,
                      transition: "all 0.3s ease",
                      opacity: formik.values.is_weight_based ? 0.5 : 1,
                    }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600} color={formik.values.pos_requires_quantity ? "#8b5cf6" : "#94a3b8"}>
                          Require Quantity Entry
                        </Typography>
                        <Typography fontSize={10} color="#94a3b8">
                          Prompts cashier to enter quantity before adding
                        </Typography>
                      </Box>
                      <Switch size="small"
                        checked={Boolean(formik.values.pos_requires_quantity)}
                        disabled={Boolean(formik.values.is_weight_based)}
                        onChange={(_, v) => formik.setFieldValue("pos_requires_quantity", v)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#8b5cf6" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#8b5cf6" },
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Section>

              {/* E. Inventory Control */}
              <Section
                icon={<InventoryIcon sx={{ fontSize: 18, color: "#16a34a" }} />}
                title="Inventory Control"
                accentColor="#16a34a"
                subtitle="Stock tracking and reorder settings"
                collapsible
                defaultOpen={false}
              >
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5, mb: 2,
                  p: 2, borderRadius: 2.5,
                  bgcolor: inventoryForm.track_inventory ? alpha("#16a34a", 0.04) : "#f8fafc",
                  border: `1px solid ${inventoryForm.track_inventory ? alpha("#16a34a", 0.15) : "#e2e8f0"}`,
                  transition: "all 0.3s ease",
                }}>
                  <Switch size="small" checked={inventoryForm.track_inventory} onChange={(_, v) => setInventoryForm(f => ({ ...f, track_inventory: v }))}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#16a34a" },
                    }}
                  />
                  <Box>
                    <Typography fontSize={13} fontWeight={600} color={inventoryForm.track_inventory ? "#16a34a" : "#94a3b8"}>
                      {inventoryForm.track_inventory ? "Inventory Tracking Enabled" : "Enable Inventory Tracking"}
                    </Typography>
                    <Typography fontSize={11} color="#94a3b8">
                      Monitor stock levels and get reorder alerts
                    </Typography>
                  </Box>
                </Box>
                <Collapse in={inventoryForm.track_inventory}>
                  <Grid container spacing={2.5}>
                    {[
                      { label: "Current Stock", key: "current_stock", color: "#3b82f6", placeholder: "Enter current stock" },
                      { label: "Minimum Stock", key: "minimum_stock", color: "#f59e0b", placeholder: "Enter minimum stock" },
                      { label: "Reorder Point", key: "reorder_point", color: "#ef4444", placeholder: "Enter reorder point" },
                      { label: "Reorder Qty",   key: "reorder_qty",   color: "#8b5cf6", placeholder: "Enter reorder qty" },
                    ].map((inv) => (
                      <Grid item xs={6} sm={3} key={inv.key}>
                        <Box sx={{
                          p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0",
                          borderTop: `3px solid ${inv.color}`,
                        }}>
                          <Typography sx={{ ...labelSx, fontSize: 10 }}>{inv.label}</Typography>
                          <TextField
                            fullWidth size="small" type="number"
                            placeholder={inv.placeholder}
                            value={(inventoryForm as any)[inv.key]}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === "" || /^\d*\.?\d*$/.test(v)) {
                                setInventoryForm(f => ({ ...f, [inv.key]: v }));
                              }
                            }}
                            inputProps={{ min: 0, inputMode: "decimal" }}
                            sx={numInputSx}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </Section>

              {/* E. Supplier Mapping */}
              <Section
                icon={<StorefrontIcon sx={{ fontSize: 18, color: "#ea580c" }} />}
                title="Supplier Mapping"
                accentColor="#ea580c"
                subtitle="Link suppliers with pricing and units"
                collapsible
                defaultOpen={false}
              >
                {supplierMappings.length === 0 && (
                  <Box sx={{
                    textAlign: "center", py: 4,
                    borderRadius: 2.5, border: "2px dashed #e2e8f0",
                    background: "linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%)",
                  }}>
                    <StorefrontIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
                    <Typography fontSize={13} color="#94a3b8" fontWeight={500}>No suppliers linked yet</Typography>
                    <Typography fontSize={11} color="#cbd5e1" mb={2}>Add suppliers to track purchase costs</Typography>
                    <Button size="small" variant="outlined" onClick={addSupplierMapping}
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                        borderColor: "#ea580c", color: "#ea580c",
                        "&:hover": { borderColor: "#ea580c", bgcolor: alpha("#ea580c", 0.04) },
                      }}>
                      Add Supplier
                    </Button>
                  </Box>
                )}
                {supplierMappings.map((m, i) => (
                  <Grow in key={i}>
                    <Box sx={{
                      display: "flex", gap: 1.5, alignItems: "flex-end", mb: 2, p: 2.5,
                      borderRadius: 2.5, bgcolor: "#fafbfc",
                      border: m.is_default ? `1px solid ${alpha("#16a34a", 0.2)}` : "1px solid #e2e8f0",
                      transition: "all 0.2s ease",
                      "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.04)" },
                    }}>
                      <Box sx={{ flex: 2 }}>
                        <Typography sx={{ ...labelSx, fontSize: 10 }}>Supplier</Typography>
                        <Autocomplete
                          fullWidth size="small"
                          options={[
                            ...suppliers,
                            { id: -1, customerCompanyName: "+ Create New Supplier" } as { id: number; customerCompanyName: string },
                          ]}
                          value={suppliers.find(s => s.id === m.supplier_id) || null}
                          getOptionLabel={(o) => o.customerCompanyName || ""}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          onChange={(_, picked) => {
                            if (!picked) {
                              const v = [...supplierMappings]; v[i].supplier_id = 0; setSupplierMappings(v);
                              return;
                            }
                            if (picked.id === -1) {
                              setSupplierCreateRowIdx(i);
                              setSupplierCreateOpen(true);
                              return;
                            }
                            const v = [...supplierMappings]; v[i].supplier_id = picked.id; setSupplierMappings(v);
                          }}
                          renderOption={(props, option) =>
                            option.id === -1 ? (
                              <li {...props} key="__create_new__" style={{
                                borderTop: "1px solid #e2e8f0",
                                marginTop: 4, paddingTop: 8,
                                color: "#00838f", fontWeight: 600, fontSize: 13,
                                display: "flex", alignItems: "center", gap: 6,
                              }}>
                                <AddIcon sx={{ fontSize: 16 }} />
                                Create New Supplier
                              </li>
                            ) : (
                              <li {...props} key={option.id} style={{ fontSize: 13 }}>{option.customerCompanyName}</li>
                            )
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select or search supplier" sx={inputSx} />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...labelSx, fontSize: 10 }}>Purchase Price</Typography>
                        <TextField
                          fullWidth size="small" type="number"
                          placeholder="Enter price"
                          value={m.supplier_price}
                          onChange={e => {
                            const raw = e.target.value;
                            if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                              const v = [...supplierMappings];
                              v[i].supplier_price = raw;
                              setSupplierMappings(v);
                            }
                          }}
                          inputProps={{ min: 0, step: "0.01", inputMode: "decimal" }}
                          sx={numInputSx}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...labelSx, fontSize: 10 }}>Unit</Typography>
                        <TextField
                          select fullWidth size="small"
                          value={m.supplier_unit || ""}
                          onChange={e => {
                            const v = [...supplierMappings];
                            v[i].supplier_unit = e.target.value;
                            setSupplierMappings(v);
                          }}
                          sx={inputSx}
                        >
                          <MenuItem value="">Select unit</MenuItem>
                          {unitList.map(u => (
                            <MenuItem key={u.id} value={u.abbreviation}>
                              {u.name} ({u.abbreviation})
                            </MenuItem>
                          ))}
                          {/* Fallback: preserve a legacy free-text value that isn't in unitList */}
                          {m.supplier_unit && !unitList.some(u => u.abbreviation === m.supplier_unit) && (
                            <MenuItem value={m.supplier_unit}>{m.supplier_unit} (legacy)</MenuItem>
                          )}
                        </TextField>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pb: 0.5 }}>
                        <Chip label={m.is_default ? "Preferred" : "Alt"} size="small" onClick={() => { const v = supplierMappings.map((x, j) => ({ ...x, is_default: j === i })); setSupplierMappings(v); }}
                          sx={{
                            height: 24, fontSize: 10, fontWeight: 700, cursor: "pointer",
                            bgcolor: m.is_default ? alpha("#16a34a", 0.08) : "#f1f5f9",
                            color: m.is_default ? "#16a34a" : "#94a3b8",
                            border: `1px solid ${m.is_default ? alpha("#16a34a", 0.2) : "transparent"}`,
                            "&:hover": { bgcolor: m.is_default ? alpha("#16a34a", 0.12) : "#e2e8f0" },
                          }} />
                        <IconButton size="small" onClick={() => setSupplierMappings(prev => prev.filter((_, j) => j !== i))}
                          sx={{
                            width: 28, height: 28,
                            "&:hover": { bgcolor: alpha("#ef4444", 0.08) },
                          }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grow>
                ))}
                {supplierMappings.length > 0 && (
                  <Button size="small" onClick={addSupplierMapping}
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      textTransform: "none", fontSize: 12, fontWeight: 600, color: "#ea580c",
                      "&:hover": { bgcolor: alpha("#ea580c", 0.04) },
                    }}>
                    Add Another Supplier
                  </Button>
                )}

                {/* Quick Create Supplier Dialog */}
                <Dialog
                  open={supplierCreateOpen}
                  onClose={() => {
                    if (supplierCreateBusy) return;
                    setSupplierCreateOpen(false);
                    setSupplierCreateError("");
                    setNewSupplierForm({ customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "" });
                  }}
                  PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}
                >
                  <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                        background: `linear-gradient(135deg, ${alpha("#ea580c", 0.12)} 0%, ${alpha("#ea580c", 0.06)} 100%)`,
                      }}>
                        <StorefrontIcon sx={{ fontSize: 16, color: "#ea580c" }} />
                      </Box>
                      Create New Supplier
                    </Box>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 2 }}>
                    <Typography sx={{ ...labelSx, mt: 1 }}>Company Name *</Typography>
                    <TextField
                      fullWidth size="small" autoFocus
                      placeholder="e.g. Kings Burfs"
                      value={newSupplierForm.customerCompanyName}
                      onChange={e => { setNewSupplierForm(f => ({ ...f, customerCompanyName: e.target.value })); setSupplierCreateError(""); }}
                      sx={inputSx}
                    />
                    <Typography sx={{ ...labelSx, mt: 2 }}>Phone</Typography>
                    <TextField
                      fullWidth size="small"
                      placeholder="Optional"
                      value={newSupplierForm.customerCompanyPhoneNumebr}
                      onChange={e => setNewSupplierForm(f => ({ ...f, customerCompanyPhoneNumebr: e.target.value }))}
                      sx={inputSx}
                    />
                    <Typography sx={{ ...labelSx, mt: 2 }}>Email</Typography>
                    <TextField
                      fullWidth size="small"
                      placeholder="Optional"
                      value={newSupplierForm.customerCompanyEmail}
                      onChange={e => setNewSupplierForm(f => ({ ...f, customerCompanyEmail: e.target.value }))}
                      sx={inputSx}
                    />
                    {supplierCreateError && (
                      <Typography sx={{ fontSize: 11, color: "#ef4444", mt: 1.5, fontWeight: 600 }}>{supplierCreateError}</Typography>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                      size="small" disabled={supplierCreateBusy}
                      onClick={() => {
                        setSupplierCreateOpen(false);
                        setSupplierCreateError("");
                        setNewSupplierForm({ customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "" });
                      }}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small" variant="contained" disableElevation
                      disabled={supplierCreateBusy || !newSupplierForm.customerCompanyName.trim()}
                      onClick={async () => {
                        setSupplierCreateBusy(true);
                        setSupplierCreateError("");
                        try {
                          const t = localStorage.getItem("token") || "";
                          const res = await fetch(`${API_URL}/vendor`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
                            body: JSON.stringify({
                              customerCompanyName: newSupplierForm.customerCompanyName.trim(),
                              customerCompanyPhoneNumebr: newSupplierForm.customerCompanyPhoneNumebr.trim() || null,
                              customerCompanyEmail: newSupplierForm.customerCompanyEmail.trim() || null,
                            }),
                          });
                          const json = await res.json();
                          if (!res.ok || json.status === false) {
                            setSupplierCreateError(json.message || "Failed to create supplier");
                            return;
                          }
                          // Reload the suppliers list and resolve the new id
                          const list = await loadSuppliers();
                          const newId = json?.data?.id ?? list.find(s => s.customerCompanyName === newSupplierForm.customerCompanyName.trim())?.id;
                          if (newId && supplierCreateRowIdx !== null) {
                            setSupplierMappings(prev => {
                              const v = [...prev];
                              if (v[supplierCreateRowIdx]) v[supplierCreateRowIdx].supplier_id = Number(newId);
                              return v;
                            });
                          }
                          showMessage("Supplier created");
                          setSupplierCreateOpen(false);
                          setNewSupplierForm({ customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "" });
                          setSupplierCreateRowIdx(null);
                        } catch {
                          setSupplierCreateError("Failed to create supplier");
                        } finally {
                          setSupplierCreateBusy(false);
                        }
                      }}
                      sx={{
                        borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                        background: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
                        "&:hover": { background: "linear-gradient(135deg, #c2410c 0%, #9a3412 100%)" },
                      }}
                    >
                      {supplierCreateBusy ? "Creating..." : "Create & Select"}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Section>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={12} lg={4}>

              {/* Product Image */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0",
                overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, rgba(0,131,143,0.04) 0%, rgba(0,131,143,0.01) 100%)",
                  borderBottom: "1px solid rgba(0,131,143,0.08)",
                  px: 3, py: 2.5,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(0,131,143,0.12) 0%, rgba(0,131,143,0.06) 100%)",
                    }}>
                      <ImageIcon sx={{ fontSize: 18, color: "#00838f" }} />
                    </Box>
                    <Box>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">Product Image</Typography>
                      <Typography fontSize={11} color="#94a3b8">Upload a photo of your product</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Box sx={{
                    width: "100%", aspectRatio: "4/3", borderRadius: 3,
                    bgcolor: selectedImage ? "#000" : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    background: selectedImage ? "#000" : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    border: selectedImage ? "none" : "2px dashed #d1d5db",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    overflow: "hidden", position: "relative", mb: 2, cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": selectedImage ? {} : {
                      borderColor: "#00838f", bgcolor: alpha("#00838f", 0.02),
                      "& .upload-icon": { transform: "translateY(-4px)", color: "#00838f" },
                    },
                  }}>
                    {selectedImage ? (
                      <>
                        <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 30%)",
                          opacity: 0, transition: "opacity 0.2s ease",
                          "&:hover": { opacity: 1 },
                          display: "flex", alignItems: "flex-start", justifyContent: "flex-end", p: 1.5,
                        }}>
                          <IconButton size="small" onClick={() => setSelectedImage(undefined)}
                            sx={{
                              bgcolor: "rgba(255,255,255,0.9)", color: "#ef4444",
                              width: 32, height: 32,
                              "&:hover": { bgcolor: "#fff" },
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </>
                    ) : existingImage ? (
                      <>
                        <img src={`${LARAVEL_API_URL}/${existingImage}`} alt="Product" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 40%)", opacity: 0, "&:hover": { opacity: 1 }, transition: "opacity 0.2s", display: "flex", alignItems: "flex-start", justifyContent: "flex-end", p: 1.5 }}>
                          <IconButton size="small" onClick={() => setExistingImage("")} sx={{ bgcolor: "rgba(255,255,255,0.9)", color: "#ef4444", width: 32, height: 32, "&:hover": { bgcolor: "#fff" }, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </>
                    ) : (
                      <label htmlFor="product-img" style={{ cursor: "pointer", textAlign: "center", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <CloudUploadIcon className="upload-icon" sx={{ fontSize: 40, color: "#cbd5e1", mb: 1.5, transition: "all 0.3s ease" }} />
                        <Typography fontSize={13} color="#64748b" fontWeight={600}>Click to upload</Typography>
                        <Typography fontSize={11} color="#94a3b8" mt={0.5}>JPG, PNG, GIF, WEBP up to 2MB</Typography>
                      </label>
                    )}
                  </Box>
                  <input accept="image/*" style={{ display: "none" }} id="product-img" type="file" onChange={handleImageChange} />
                  {!selectedImage && !existingImage && (
                    <label htmlFor="product-img">
                      <Button component="span" fullWidth variant="outlined" size="small" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                          borderColor: "#e2e8f0", color: "#64748b", py: 1,
                          "&:hover": { borderColor: "#00838f", color: "#00838f", bgcolor: alpha("#00838f", 0.02) },
                          transition: "all 0.2s ease",
                        }}>
                        Browse Files
                      </Button>
                    </label>
                  )}
                </Box>
              </Paper>

              {/* Sales Channels */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, rgba(16,163,127,0.04) 0%, rgba(16,163,127,0.01) 100%)",
                  borderBottom: "1px solid rgba(16,163,127,0.08)",
                  px: 3, py: 2.5,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(16,163,127,0.12) 0%, rgba(16,163,127,0.06) 100%)",
                    }}>
                      <TuneIcon sx={{ fontSize: 18, color: "#10a37f" }} />
                    </Box>
                    <Box>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">Sales Channels</Typography>
                      <Typography fontSize={11} color="#94a3b8">Where this product is available</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 2 }}>
                  {[
                    { label: "Available Online", field: "productViewOnline", color: "#16a34a", icon: "O" },
                    { label: "Available in POS", field: "productViewPos", color: "#3b82f6", icon: "P" },
                    { label: "Can Sell", field: "productCanSell", color: "#8b5cf6", icon: "S" },
                    { label: "Active", field: "isActive", color: "#0891b2", icon: "A" },
                  ].map(ch => (
                    <Box key={ch.field} sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      py: 1.5, px: 1.5, mx: -0.5, borderRadius: 2,
                      transition: "all 0.15s ease",
                      "&:hover": { bgcolor: alpha(ch.color, 0.03) },
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: (formik.values as any)[ch.field] ? alpha(ch.color, 0.1) : "#f1f5f9",
                          transition: "all 0.2s ease",
                        }}>
                          <Typography fontSize={10} fontWeight={800} color={(formik.values as any)[ch.field] ? ch.color : "#94a3b8"}>
                            {ch.icon}
                          </Typography>
                        </Box>
                        <Typography fontSize={13} color="#334155" fontWeight={500}>{ch.label}</Typography>
                      </Box>
                      <Switch size="small" checked={Boolean((formik.values as any)[ch.field])} onChange={(_, v) => formik.setFieldValue(ch.field, v)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: ch.color },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: ch.color },
                        }} />
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* POS Settings */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(99,102,241,0.01) 100%)",
                  borderBottom: "1px solid rgba(99,102,241,0.08)",
                  px: 3, py: 2.5,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.06) 100%)",
                    }}>
                      <ColorLensIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                    </Box>
                    <Box>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">POS Settings</Typography>
                      <Typography fontSize={11} color="#94a3b8">Button appearance and modifiers</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Typography sx={{ ...labelSx, fontSize: 11 }}>Button Color</Typography>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
                    <Box
                      onClick={() => setShowColorPicker(v => !v)}
                      sx={{
                        width: 40, height: 40, borderRadius: 2.5, bgcolor: selectedColor,
                        border: "2px solid #e2e8f0", cursor: "pointer",
                        boxShadow: `0 2px 8px ${alpha(selectedColor, 0.3)}`,
                        transition: "all 0.2s ease",
                        "&:hover": { transform: "scale(1.08)", boxShadow: `0 4px 16px ${alpha(selectedColor, 0.4)}` },
                      }}
                    />
                    <TextField size="small" value={selectedColor} onChange={e => setSelectedColor(e.target.value)} sx={{ flex: 1, ...inputSx }} />
                  </Box>
                  <Collapse in={showColorPicker}>
                    <Box ref={colorRef} sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
                      <HexColorPicker color={selectedColor} onChange={setSelectedColor} style={{ width: "100%" }} />
                    </Box>
                  </Collapse>

                  <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                  <Typography sx={{ ...labelSx, fontSize: 11 }}>Has Modifier</Typography>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, borderRadius: 2,
                    bgcolor: formik.values.isModifier ? alpha("#16a34a", 0.04) : "#fafafa",
                    border: `1px solid ${formik.values.isModifier ? alpha("#16a34a", 0.15) : "#f1f5f9"}`,
                    transition: "all 0.2s ease",
                  }}>
                    <Switch size="small" checked={formik.values.isModifier as boolean} onChange={(_, v) => formik.setFieldValue("isModifier", v)} />
                    <Typography fontSize={12} color={formik.values.isModifier ? "#16a34a" : "#94a3b8"} fontWeight={600}>{formik.values.isModifier ? "Yes" : "No"}</Typography>
                  </Box>
                  {Boolean(formik.values.isModifier) && modifierGroupList?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ ...labelSx, fontSize: 11 }}>Modifier Group</Typography>
                      <TextField select fullWidth size="small" value={formik.values.modifierGroupId?.[0] || ""} onChange={e => formik.setFieldValue("modifierGroupId", e.target.value ? [Number(e.target.value)] : [])} sx={inputSx}>
                        <MenuItem value="">None</MenuItem>
                        {modifierGroupList.map(g => <MenuItem key={g.id} value={g.id}>{g.modifierGroupName}</MenuItem>)}
                      </TextField>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Advanced */}
              <Paper elevation={0} sx={{
                mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden",
                "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
                transition: "all 0.3s ease",
              }}>
                <Box sx={{
                  background: showAdvanced
                    ? "linear-gradient(135deg, rgba(100,116,139,0.06) 0%, rgba(100,116,139,0.02) 100%)"
                    : "linear-gradient(135deg, rgba(100,116,139,0.03) 0%, rgba(100,116,139,0.01) 100%)",
                  px: 3, py: 2.5, cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { background: "linear-gradient(135deg, rgba(100,116,139,0.06) 0%, rgba(100,116,139,0.02) 100%)" },
                }}
                  onClick={() => setShowAdvanced(v => !v)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, rgba(100,116,139,0.12) 0%, rgba(100,116,139,0.06) 100%)",
                    }}>
                      <SettingsIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontSize={15} fontWeight={700} color="#0f172a">Advanced</Typography>
                      <Typography fontSize={11} color="#94a3b8">Serialization and tags</Typography>
                    </Box>
                    <ExpandMoreIcon sx={{
                      fontSize: 20, color: "#94a3b8",
                      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: showAdvanced ? "rotate(180deg)" : "rotate(0)",
                    }} />
                  </Box>
                </Box>
                <Collapse in={showAdvanced}>
                  <Box sx={{ p: 3, borderTop: "1px solid #f1f5f9" }}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          p: 1.5, borderRadius: 2,
                          bgcolor: formik.values.isSerialize ? alpha("#8b5cf6", 0.04) : "#fafafa",
                          border: `1px solid ${formik.values.isSerialize ? alpha("#8b5cf6", 0.15) : "#f1f5f9"}`,
                          transition: "all 0.2s ease",
                        }}>
                          <Switch size="small" checked={formik.values.isSerialize as boolean} onChange={(_, v) => formik.setFieldValue("isSerialize", v)}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color: "#8b5cf6" },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#8b5cf6" },
                            }}
                          />
                          <Box>
                            <Typography fontSize={13} color="#334155" fontWeight={500}>Serialized Product</Typography>
                            <Typography fontSize={10} color="#94a3b8">Track individual units by serial number</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={{ ...labelSx, fontSize: 11 }}>Tags</Typography>
                        <Autocomplete
                          multiple fullWidth size="small"
                          options={productTagList || []}
                          getOptionLabel={(o) => o.tagName || ""}
                          isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                          value={(productTagList || []).filter((t) =>
                            (formik.values.productTagIds || []).map(Number).includes(Number(t.id))
                          )}
                          onChange={(_, picked) =>
                            formik.setFieldValue("productTagIds", picked.map((p) => p.id))
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                size="small"
                                label={option.tagName}
                                {...getTagProps({ index })}
                                key={option.id}
                                sx={{ bgcolor: alpha("#8b5cf6", 0.1), color: "#7c3aed", fontWeight: 600, fontSize: 11 }}
                              />
                            ))
                          }
                          noOptionsText={
                            (productTagList || []).length === 0
                              ? "No tags created yet — add one in Inventory → Product Tags"
                              : "No matches"
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder={(productTagList || []).length === 0 ? "No tags available" : "Select or search tags"} sx={inputSx} />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>

              {/* Save Button */}
              <Paper elevation={0} sx={{
                p: 3, borderRadius: 3, border: "1px solid #e2e8f0",
                background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
              }}>
                <Button fullWidth variant="contained" disableElevation disabled={!formValid || isLoading}
                  onClick={() => formik.handleSubmit()}
                  startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                  sx={{
                    borderRadius: 3, py: 1.8, textTransform: "none", fontWeight: 700, fontSize: 15,
                    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                    boxShadow: "0 4px 20px rgba(15,23,42,0.25)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                      boxShadow: "0 6px 30px rgba(15,23,42,0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                    transition: "all 0.2s ease",
                  }}>
                  {isLoading ? "Saving..." : isEdit ? "Update Product" : "Save Product"}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

// Default export for Create page (backward compat)
const Index = () => <ProductForm mode="create" />;
export default Index;
export { ProductForm };
